import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HistorySection as HistorySectionModel } from '@/types/public/history-page';
import { ContentType } from '@/types/common/section-contents';
import { SectionTemplate } from '@/types/common/sections';
import { getImageSrc } from '@/utils/functions/image-helper/image-helper';
import { YEAR_PATTERN, YEAR_ONLY_PATTERN } from '@/const/public/history-page';
import { useGetLocalization } from '@/hooks/common/use-get-localization/useGetLocalization';
import { useScrollAnimation } from '@/hooks/common/use-scroll-animation/useScrollAnimation';
import { HistoryQuadImages } from './HistoryQuadImages';
import { HistoryDualImages } from './HistoryDualImages';
import { HistoryTripleImages } from './HistoryTripleImages';
import styles from './HistorySection.module.scss';

interface HistorySectionProps {
    section: HistorySectionModel;
    showYearLabel?: boolean;
}

interface HistorySectionHeaderProps {
    displayTitle: string;
    description?: string | null;
}

const HistorySectionHeader = ({ displayTitle, description }: HistorySectionHeaderProps) => {
    if (!displayTitle && !description) return null;

    return (
        <div className={styles['section-header']}>
            <div className={styles['title-area']}>
                {displayTitle && <h2 className={styles['section-title']}>{displayTitle}</h2>}
            </div>
            {description && <p className={styles['section-description']}>{description}</p>}
        </div>
    );
};

export const HistorySection = ({ section, showYearLabel = true }: HistorySectionProps) => {
    const { t } = useTranslation('historyPage');
    const { ref, isVisible } = useScrollAnimation();

    const titleContent = section.contents.find((c) => c.contentType === ContentType.Title);
    const descContent = section.contents.find((c) => c.contentType === ContentType.Description);

    const titleFallback = useMemo(() => ({ title: titleContent?.title }), [titleContent?.title]);
    const descFallback = useMemo(() => ({ description: descContent?.description }), [descContent?.description]);

    const { title: localizedTitle } = useGetLocalization(titleContent?.localizations, titleFallback);
    const { description } = useGetLocalization(descContent?.localizations, descFallback);

    const rawTitle = localizedTitle ?? '';
    const images = section.contents
        .filter((c) => c.contentType === ContentType.Image)
        .sort((a, b) => a.order - b.order)
        .map((c) => c.image ?? null);

    const yearMatch = rawTitle.match(YEAR_PATTERN);
    const yearMatchStandalone = !yearMatch ? rawTitle.match(YEAR_ONLY_PATTERN) : null;
    const extractedYear = yearMatch?.[1] ?? yearMatchStandalone?.[1] ?? null;
    const yearLabel = extractedYear ? extractedYear + t('YEAR_SUFFIX') : null;
    const displayTitle = (yearMatch ? rawTitle.replace(yearMatch[0], '') : rawTitle).toUpperCase();

    const hasRealImages = images.some((img) => img !== null);
    const wrapperClass = `${styles['section-wrapper']} ${isVisible ? styles.visible : ''}`;

    const yearBadge = yearLabel && showYearLabel ? <span className={styles['year-label']}>{yearLabel}</span> : null;

    if (section.template === SectionTemplate.SingleImageRight) {
        const imageSrc = hasRealImages ? getImageSrc(images[0]) : null;
        return (
            <section ref={ref} className={wrapperClass}>
                {yearBadge}
                <div className={styles['single-image-right']}>
                    <div className={styles['text-column']}>
                        {displayTitle && <h2 className={styles['section-title']}>{displayTitle}</h2>}
                        {description && <p className={styles['section-description']}>{description}</p>}
                    </div>
                    {imageSrc && (
                        <div className={styles['image-column']}>
                            <img src={imageSrc} alt="" className={styles['section-image']} loading="lazy" />
                        </div>
                    )}
                </div>
            </section>
        );
    }

    if (section.template === SectionTemplate.SingleImageTop && hasRealImages) {
        const imageSrc = getImageSrc(images[0]);
        return (
            <section ref={ref} className={wrapperClass}>
                {yearBadge}
                {imageSrc && <img src={imageSrc} alt="" className={styles['full-width-image']} loading="lazy" />}
                <div className={styles['section-header']}>
                    <div className={styles['title-area']}>
                        {displayTitle && <h2 className={styles['section-title']}>{displayTitle}</h2>}
                    </div>
                    {description && <p className={styles['section-description']}>{description}</p>}
                </div>
            </section>
        );
    }

    if (section.template === SectionTemplate.SingleImageBottom && hasRealImages) {
        const imageSrc = getImageSrc(images[0]);
        return (
            <section ref={ref} className={wrapperClass}>
                {yearBadge}
                {(displayTitle || description) && (
                    <HistorySectionHeader displayTitle={displayTitle} description={description} />
                )}
                {imageSrc && <img src={imageSrc} alt="" className={styles['full-width-image']} loading="lazy" />}
            </section>
        );
    }
    return (
        <section ref={ref} className={wrapperClass}>
            {yearBadge}
            {(displayTitle || description) && (
                <HistorySectionHeader displayTitle={displayTitle} description={description} />
            )}
            {hasRealImages && (
                <div className={styles['section-images']}>
                    {section.template === SectionTemplate.QuadImagesBottom && <HistoryQuadImages images={images} />}
                    {section.template === SectionTemplate.DualImagesBottom && <HistoryDualImages images={images} />}
                    {section.template === SectionTemplate.TripleImagesBottom && <HistoryTripleImages images={images} />}
                </div>
            )}
        </section>
    );
};
